import { useRef, useState, type ReactNode } from 'react';
import { FileCard, theme } from '@agentisai/design-system';
import type { ArtifactListItem } from '@agentisai/shared-types';
import { formatArtifactMeta } from '../lib/formatArtifactMeta';
import { useArtifacts } from '../queries/useArtifacts';
import { RailNewButton } from './RailNewButton';
import { RemoveArtifactDialog } from './RemoveArtifactDialog';

const mutedMessage = {
  padding: theme.space[4],
  color: theme.color.neutral.faint,
  fontSize: theme.typography.fontSize.bodySm,
  lineHeight: theme.typography.lineHeight.body,
} as const;

/** View is wired in S11 — inert here. */
const noop = () => undefined;

export interface FilesRailBodyProps {
  /** The selected workstream whose artifacts to list. The Files panel is mounted
   * only while a workstream is selected (WorkspaceShell / S08), so this is set. */
  readonly workstreamId: string;
}

/**
 * Files rail panel body (Drop 6 / S09). Slots into `WorkspaceShell.filesPanel` and
 * lists the selected workstream's agent-produced artifacts as `FileCard`s
 * (filename + a derived `type · size · date` meta line). Sibling to
 * `WorkstreamsRailBody`: same loading / error / empty idiom; the query
 * (`useArtifacts`) is keyed by workstream so it refetches on selection change and
 * S10's delete can invalidate it. View / Remove land in S11 / S10.
 */
export function FilesRailBody({ workstreamId }: FilesRailBodyProps) {
  const query = useArtifacts(workstreamId);
  // The file awaiting delete-confirmation (S10). Component-local + ephemeral —
  // one subtree owns it (frontend-architecture R1.0); the confirm Modal mounts
  // only while it is set. On CANCEL the DS `useDialog` cleanup restores focus to
  // the triggering card for free (it's still there); the SUCCESS path is handled
  // by `handleDeleted` below (the card is gone by then).
  const [pendingRemoval, setPendingRemoval] = useState<ArtifactListItem | null>(null);
  // A stable, always-rendered wrapper used as the focus landing after a delete.
  const bodyRef = useRef<HTMLDivElement>(null);

  function handleDeleted() {
    setPendingRemoval(null);
    // The removed card's Remove button unmounts on the refetch, so the confirm
    // dialog's focus-restore (useDialog) would drop focus to <body>. Move focus
    // into the Files panel body on the next frame — after the dialog's unmount +
    // restore commit — so a keyboard / screen-reader user keeps context instead
    // of being stranded at the top of the document.
    requestAnimationFrame(() => bodyRef.current?.focus());
  }

  let content: ReactNode;
  if (query.isLoading) {
    content = <div style={mutedMessage}>Loading files…</div>;
  } else if (query.isError) {
    content = (
      <div role="alert" style={mutedMessage}>
        Couldn't load files. We'll retry automatically.
      </div>
    );
  } else {
    // Newest first (R2 — derived at render). The agent lists ledger/append
    // order, so sort by `lastModified` desc here; spread first so we never
    // mutate the React Query cache's array in place.
    const items = [...(query.data?.data.artifacts ?? [])].sort(
      (a, b) => Date.parse(b.lastModified) - Date.parse(a.lastModified),
    );
    content =
      items.length === 0 ? (
        <div style={mutedMessage}>Files an agent creates in this workstream will appear here.</div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space[2],
            padding: `0 ${theme.space[4]}`,
          }}
        >
          {items.map((item) => (
            <FileCard
              key={item.artifactId}
              filename={item.filename}
              meta={formatArtifactMeta(item)}
              onView={noop}
              onRemove={() => setPendingRemoval(item)}
            />
          ))}
        </div>
      );
  }

  return (
    <div ref={bodyRef} tabIndex={-1} style={{ outline: 'none' }}>
      {content}
      {pendingRemoval && (
        <RemoveArtifactDialog
          workstreamId={workstreamId}
          artifact={pendingRemoval}
          onClose={() => setPendingRemoval(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
}
FilesRailBody.displayName = 'FilesRailBody';

/**
 * The Files rail's "+ New" subheader action (mirrors the Workstreams "+ New"
 * pill). File upload is descoped in Drop 6, so this is intentionally **inert**
 * for now — it carries a "coming soon" hint and gets its real handler when the
 * upload path lands; rendered as the Files `RailPanel`'s `subheaderAction`.
 */
export function NewFileAction() {
  return (
    <RailNewButton onClick={() => undefined} title="Uploading files is coming soon">
      + New
    </RailNewButton>
  );
}
NewFileAction.displayName = 'NewFileAction';
